import React, { useCallback, useEffect, useImperativeHandle, useMemo, forwardRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  Typography
} from '@mui/material';
import useAuth from 'hooks/useAuth';
import { useBillingClient } from 'client/client-hook';
import {
  CHECKOUT_COPY,
  computeUseTrial,
  resolveCryptoPurchaseTotal,
  resolveCatalogPrice,
  sumDedicatedEnvCents,
  getUnitAmountCents,
  SIGNAL_MANAGER_PRODUCT,
  BILLING_CYCLES
} from 'lib/billing';
import { buildCheckoutPlan } from './checkoutPlan';
import { useInvoicePreview } from '../store';
import { useCheckoutState, useCheckoutStore } from '../store';
import { collectCryptoLineItems, planToCryptoRequest } from './cryptoPlanTranslator';
import { NotePanel } from '../NotePanel';
import { CancelCryptoPaymentReply, CreateCryptoPaymentReply, HasPendingCryptoPaymentReply } from 'proto/billing-manager_pb';

const CRYPTO_COPY = CHECKOUT_COPY.payment.crypto;

export type CryptoHandle = {
  // Submit crypto payment. Returns true on successful purchase_url open.
  submit: () => Promise<boolean>;
};

type Props = {
  onError?: (msg: string) => void;
};

type PendingState = {
  loading: boolean;
  pending: boolean;
  status: string;
  purchaseUrl: string;
  paymentId: string;
};

// Statuses the backend allows the user to cancel — kept in sync with
// cancelableCryptoStatuses in pkg/server/endpoints.go. Any status outside
// this set ("paid", "partially_paid", "refunded") leaves the cancel button
// hidden because the operation would either lose money or not apply.
const CANCELABLE_STATUSES = new Set(['new', 'pending', 'confirming']);

export const CryptoElement = forwardRef<CryptoHandle, Props>(function CryptoElement({ onError }, ref) {
  const { user } = useAuth() as any;
  const getBillingClient = useBillingClient();
  const state = useCheckoutState();
  // Discount code from TotalSummary — rides the crypto createSubscription
  // payload exactly like toltReferral does.
  const coupon = useCheckoutStore((s) => s.coupon);
  const [pendingState, setPendingState] = useState<PendingState>({
    loading: true,
    pending: false,
    status: '',
    purchaseUrl: '',
    paymentId: ''
  });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);

  // Single source of truth for the resolved cart plan. Both the itemized
  // preview rendering AND the submit handler read it; computing it twice
  // (once per useMemo, once per submit) risked drift if the resolver or its
  // inputs ever changed shape.
  const plan = useMemo(
    () => buildCheckoutPlan(state).plan,
    [
      (state as any).signalManagerSelected,
      state.billingCycle,
      state.standardCount,
      state.premiumCount,
      state.futuresCount,
      (state as any).futuresUnlimited,
      state.analyzerEnabled,
      (state as any).mcpEnabled,
      (state as any).mcpStandalone,
      state.dedicatedEnvs,
      state.catalog,
      state.subscriptions,
      state.currentAddons,
      (state as any).trialUsedProductIds,
      (state as any).skipTrial
    ]
  );

  // Standalone room: flat monthly package price (qty 1), charged in full like a new DE sub.
  const signalManagerSelected = Boolean((state as any).signalManagerSelected);
  const signalManagerPrice = signalManagerSelected
    ? resolveCatalogPrice(state.catalog, SIGNAL_MANAGER_PRODUCT, BILLING_CYCLES.monthly)
    : null;
  const signalManagerCents = signalManagerPrice ? getUnitAmountCents(signalManagerPrice) : 0;

  // Live invoice preview is the source-of-truth for the actual amount the
  // customer must pay via Coingate. It already factors in applied customer
  // balance, proration credits, and discounts. Raw `state.grandTotalCycleCents`
  // is the pre-credit catalog total and would over-charge any user with an
  // existing Stripe balance.
  const { preview, loading: previewLoading, quotedCoupon, quotedForCrypto } = useInvoicePreview();

  // Adding a NEW Dedicated Environment to an existing account is a brand-new
  // subscription (full first period, no proration), so the live "modify"
  // preview — which previews changes to the EXISTING sub — doesn't apply and
  // its dueNow stays null. Its cost is deterministic from the catalog, so
  // compute it directly and skip the preview requirement for this case.
  const newDedicatedCents = sumDedicatedEnvCents(
    {
      envs: (state.dedicatedEnvs as number[]) || [],
      kinds: (state.dedicatedEnvKinds as string[]) || [],
      subIds: (state.dedicatedEnvSubIds as (string | null)[]) || []
    },
    (state as any).billingCycle,
    Number((state as any).paidMonths) || 1,
    { skipOwned: true }
  );
  const planOps = ((plan as any)?.ops as any[]) || [];
  const isNewSubOnly =
    (plan as any)?.type === 'multi-cycle' &&
    planOps.length > 0 &&
    planOps.every((op: any) => (op.modifies?.length || 0) === 0 && (op.adds?.length || 0) === 0);
  // Crypto quotes differ from card ones when an offer waits or a line is added (see useInvoicePreviewSync).
  const quotedOwnWayForCrypto = Boolean(preview?.cancelOfferPercentBps) || planOps.some((op: any) => (op.adds?.length || 0) > 0);
  // Existing subscriber whose only change is new sub(s) (DE) — no proration to
  // preview; charge the catalog amount directly.
  const isDedicatedOnlyModify = Boolean(state.activeSubscription?.id) && isNewSubOnly && newDedicatedCents > 0;

  // A room is create-only at a flat catalog price — no proration and no preview to
  // reconcile against, so it short-circuits ahead of the DE/preview resolution.
  const purchaseTotalCents = signalManagerSelected
    ? signalManagerCents
    : resolveCryptoPurchaseTotal({
        // Catalog shortcut for a DE-only addition — surrendered as soon as the
        // settled preview carries a discount, since Coingate must collect what
        // Stripe will invoice (see resolveCryptoPurchaseTotal).
        dedicatedOnlyCatalogCents: isDedicatedOnlyModify ? newDedicatedCents : 0,
        authCheckout: Boolean(state.authCheckout),
        activeSubscription: state.activeSubscription,
        invoicePreview: preview,
        invoiceLoading: previewLoading,
        grandTotalCycleCents: state.grandTotalCycleCents || 0,
        availableCredit: Number((state.billingOverview as any)?.availableCredit || 0),
        // Same couponApplied opt-in as useAmountDueNow (incl. the
        // quoted-with-this-code guard): what Coingate charges must match the
        // discounted figure the summary/PaymentStep show — the coupon rides
        // the createSubscription payload below, so the discounted Stripe
        // invoice is what the credited balance has to cover.
        couponApplied: Boolean(coupon) && quotedCoupon === coupon
      });

  // Shared fetch so both the initial probe (useEffect) and the post-cancel
  // refresh hit the same code path. Returns nothing — state is the side
  // effect.
  const refreshPendingState = useCallback(async () => {
    if (!user?.tcId) return;
    try {
      const client = await getBillingClient();
      const resp = (await client.hasPendingCryptoPayment({
        userId: user.tcId
      })) as HasPendingCryptoPaymentReply.AsObject;
      setPendingState({
        loading: false,
        pending: Boolean(resp?.hasPendingCryptoPayment),
        status: resp?.status || '',
        purchaseUrl: resp?.purchaseUrl || '',
        paymentId: resp?.paymentId || ''
      });
    } catch (e: any) {
      setPendingState({ loading: false, pending: false, status: '', purchaseUrl: '', paymentId: '' });
      onError?.(e?.message || CRYPTO_COPY.errors.statusFailed);
    }
  }, [user?.tcId, getBillingClient, onError]);

  useEffect(() => {
    refreshPendingState();
  }, [refreshPendingState]);

  const handleCancelPending = useCallback(async () => {
    if (!user?.tcId || !pendingState.paymentId) return;
    setCanceling(true);
    try {
      const client = await getBillingClient();
      const resp = (await client.cancelCryptoPayment({
        userId: user.tcId,
        paymentId: pendingState.paymentId
      })) as CancelCryptoPaymentReply.AsObject;
      if (!resp?.canceled) {
        onError?.(CRYPTO_COPY.errors.cancelFailed);
        return;
      }
      setCancelDialogOpen(false);
      await refreshPendingState();
    } catch (e: any) {
      onError?.(e?.message || CRYPTO_COPY.errors.cancelFailed);
    } finally {
      setCanceling(false);
    }
  }, [user?.tcId, pendingState.paymentId, getBillingClient, onError, refreshPendingState]);

  useImperativeHandle(
    ref,
    () => ({
      async submit() {
        if (!user?.tcId) return false;
        if (pendingState.pending) {
          onError?.(CRYPTO_COPY.errors.alreadyPending);
          return false;
        }

        // Preview is shared across Configure + Payment via InvoicePreviewProvider
        // and already reflects current selection by the time the user lands on
        // this step. Use the shared value directly — no extra PreviewCheckout
        // RPC here. Edge case: if the user advanced before preview resolved,
        // refuse the submit instead of falling back to catalog grandTotal
        // (which would over-charge any user with a customer balance).
        // A DE-only addition has a deterministic catalog cost (handled above) and
        // creates a new sub rather than reprising the existing one, so it doesn't
        // need — and won't get — a live proration preview. Only require the
        // preview for changes to the existing subscription.
        const needsLivePreview =
          Boolean(state.authCheckout && state.activeSubscription?.id) && !isDedicatedOnlyModify && !signalManagerSelected;
        if (needsLivePreview && (previewLoading || preview?.dueNow == null)) {
          onError?.(CRYPTO_COPY.errors.previewLoading);
          return false;
        }
        // A code changes what Stripe will invoice, so its quote must have
        // settled against THIS code before Coingate is asked for an amount —
        // otherwise the order goes out at list price while the drawer (and the
        // subscription the cron creates) say discounted.
        if (coupon && (previewLoading || !preview || quotedCoupon !== coupon)) {
          onError?.(CRYPTO_COPY.errors.previewLoading);
          return false;
        }
        // A card-tab quote (just switched) would send Coingate a different amount than the invoice the cron creates.
        if (quotedOwnWayForCrypto && (previewLoading || !quotedForCrypto)) {
          onError?.(CRYPTO_COPY.errors.previewLoading);
          return false;
        }

        // Crypto sends ONE createSubscription with a single use_trial; the crypto
        // cron then splits it into one Stripe sub per item. It can't carry a
        // per-item trial flag like the card path, so fail safe: only request the
        // trial when EVERY merged item (creates + adds) is collectively
        // trial-eligible. A mixed cart (Futures + Premium) → use_trial=false, so
        // a paid product can never ride the trial free. Worst case Futures simply
        // pays on a mixed crypto cart — never a loss.
        const cryptoItems = collectCryptoLineItems(plan as any).map((i) => ({ price_id: i.priceId, quantity: i.quantity }));
        const cryptoUseTrial =
          !signalManagerSelected &&
          !(state as any).skipTrial &&
          computeUseTrial(cryptoItems, state.catalog, ((state as any).trialUsedProductIds as string[]) || []);

        const req = planToCryptoRequest({
          userId: user.tcId,
          plan: plan as any,
          // purchaseTotal = what Coingate charges. creditPurchaseTotal = what
          // gets credited to Stripe customer balance. For now these are equal
          // (no fee, no promo): the customer pays exactly what their post-
          // credit invoice owes and that full amount lands on Stripe balance
          // to immediately fund the new invoice.
          purchaseTotalCents,
          creditPurchaseTotalCents: purchaseTotalCents,
          useTrial: cryptoUseTrial,
          toltReferral: (window as any).tolt_referral || '',
          coupon
        });

        if (!req) {
          onError?.(CRYPTO_COPY.errors.noChanges);
          return false;
        }
        const client = await getBillingClient();
        const resp = (await client.createCryptoPayment(req)) as CreateCryptoPaymentReply.AsObject;
        if (!resp?.purchaseUrl) {
          onError?.(CRYPTO_COPY.errors.initFailed);
          return false;
        }

        // Match legacy UpgradePlanDrawer behavior: redirect the current tab
        // to the hosted Coinbase checkout. Subscription updates on chain
        // confirmation via webhook.
        window.location.assign(resp.purchaseUrl);
        return true;
      }
    }),
    [
      user?.tcId,
      pendingState.pending,
      state,
      getBillingClient,
      onError,
      plan,
      coupon,
      preview,
      previewLoading,
      quotedOwnWayForCrypto,
      quotedForCrypto,
      purchaseTotalCents,
      isDedicatedOnlyModify
    ]
  );

  if (pendingState.loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          {CRYPTO_COPY.checking}
        </Typography>
      </Stack>
    );
  }

  if (pendingState.pending) {
    const canCancel = Boolean(pendingState.paymentId) && CANCELABLE_STATUSES.has(pendingState.status);
    return (
      <>
        <NotePanel severity="info" title={CRYPTO_COPY.pendingTitle} body={CRYPTO_COPY.pendingDesc(pendingState.status)}>
          {(pendingState.purchaseUrl || canCancel) && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
              {pendingState.purchaseUrl ? (
                <Link href={pendingState.purchaseUrl} target="_blank" rel="noopener" underline="hover" variant="caption">
                  {CRYPTO_COPY.openPending}
                </Link>
              ) : (
                <span />
              )}
              {canCancel && (
                <Link
                  component="button"
                  type="button"
                  color="error"
                  underline="hover"
                  variant="caption"
                  onClick={() => setCancelDialogOpen(true)}
                  disabled={canceling}
                  sx={{ background: 'none', border: 0, p: 0, cursor: canceling ? 'default' : 'pointer' }}
                >
                  {CRYPTO_COPY.cancelButton}
                </Link>
              )}
            </Stack>
          )}
        </NotePanel>
        <Dialog
          open={cancelDialogOpen}
          onClose={() => !canceling && setCancelDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          sx={{ zIndex: (t) => t.zIndex.drawer + 1100 }}
        >
          <DialogTitle>{CRYPTO_COPY.cancelDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{CRYPTO_COPY.cancelDialog.body}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)} disabled={canceling}>
              {CRYPTO_COPY.cancelDialog.dismiss}
            </Button>
            <Button
              onClick={handleCancelPending}
              color="error"
              variant="contained"
              disabled={canceling}
              startIcon={canceling ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {CRYPTO_COPY.cancelDialog.confirm}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return <NotePanel severity="info" title={CRYPTO_COPY.title} body={CRYPTO_COPY.desc} />;
});
